// Page de gestion du catalogue produits/services
// CRUD + Import CSV/Excel

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Plus, Upload, Search, Edit2, Trash2, 
  X, Save, Image, DollarSign, Clock, Tag, Check,
  FileSpreadsheet, AlertCircle, ChevronDown
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const ITEM_TYPES = [
  { value: 'service', label: 'Service/Prestation' },
  { value: 'product', label: 'Produit' },
  { value: 'package', label: 'Forfait/Pack' }
];

const CATEGORIES = [
  'Installation', 'Réparation', 'Entretien', 'Rénovation', 
  'Construction', 'Dépannage', 'Conseil', 'Autre'
];

// Form Modal
function ItemFormModal({ item, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    description: item?.description || '',
    item_type: item?.item_type || 'service',
    category: item?.category || '',
    price: item?.price || '',
    price_unit: item?.price_unit || '€',
    price_min: item?.price_min || '',
    price_max: item?.price_max || '',
    duration: item?.duration || '',
    includes: item?.includes?.join('\n') || '',
    excludes: item?.excludes?.join('\n') || '',
    keywords: item?.keywords?.join(', ') || ''
  });
  const [saving, setSaving] = useState(false);
  const [usePriceRange, setUsePriceRange] = useState(!!item?.price_min || !!item?.price_max);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error('Nom et description requis');
      return;
    }

    setSaving(true);
    
    const data = {
      ...formData,
      price: formData.price ? parseFloat(formData.price) : null,
      price_min: usePriceRange && formData.price_min ? parseFloat(formData.price_min) : null,
      price_max: usePriceRange && formData.price_max ? parseFloat(formData.price_max) : null,
      includes: formData.includes.split('\n').filter(s => s.trim()),
      excludes: formData.excludes.split('\n').filter(s => s.trim()),
      keywords: formData.keywords.split(',').map(s => s.trim()).filter(Boolean)
    };
    
    if (!usePriceRange) {
      data.price_min = null;
      data.price_max = null;
    }

    await onSave(data);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {item ? 'Modifier' : 'Ajouter'} un élément
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Installation chauffe-eau"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Décris ton service/produit en détail..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={formData.item_type}
                  onChange={(e) => setFormData({ ...formData, item_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {ITEM_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catégorie
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Sélectionner...</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <DollarSign size={18} />
                Tarification
              </h3>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={usePriceRange}
                  onChange={(e) => setUsePriceRange(e.target.checked)}
                  className="rounded"
                />
                Fourchette de prix
              </label>
            </div>

            {usePriceRange ? (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Prix min</label>
                  <Input
                    type="number"
                    value={formData.price_min}
                    onChange={(e) => setFormData({ ...formData, price_min: e.target.value })}
                    placeholder="À partir de"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Prix max</label>
                  <Input
                    type="number"
                    value={formData.price_max}
                    onChange={(e) => setFormData({ ...formData, price_max: e.target.value })}
                    placeholder="Jusqu'à"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Unité</label>
                  <Input
                    value={formData.price_unit}
                    onChange={(e) => setFormData({ ...formData, price_unit: e.target.value })}
                    placeholder="€/h, €/m², etc."
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Prix</label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="Ex: 150"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Unité</label>
                  <Input
                    value={formData.price_unit}
                    onChange={(e) => setFormData({ ...formData, price_unit: e.target.value })}
                    placeholder="€, €/h, €/m², forfait"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              <Clock size={16} />
              Durée estimée
            </label>
            <Input
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              placeholder="Ex: 2h, 1 journée, 1 semaine"
            />
          </div>

          {/* Includes/Excludes */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ce qui est inclus
              </label>
              <textarea
                value={formData.includes}
                onChange={(e) => setFormData({ ...formData, includes: e.target.value })}
                placeholder="Une ligne par élément&#10;Ex: Déplacement&#10;Main d'oeuvre&#10;Garantie 2 ans"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ce qui n'est pas inclus
              </label>
              <textarea
                value={formData.excludes}
                onChange={(e) => setFormData({ ...formData, excludes: e.target.value })}
                placeholder="Une ligne par élément&#10;Ex: Pièces détachées&#10;Travaux supplémentaires"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              <Tag size={16} />
              Mots-clés (SEO)
            </label>
            <Input
              value={formData.keywords}
              onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
              placeholder="plomberie, fuite, réparation, urgent..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Séparez les mots-clés par des virgules
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  {item ? 'Enregistrer' : 'Ajouter'}
                </>
              )}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Import Modal
function ImportModal({ onClose, onImport }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const name = selectedFile.name.toLowerCase();
      if (!name.endsWith('.csv') && !name.endsWith('.xlsx')) {
        toast.error('Format non supporté. Utilisez CSV ou Excel.');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    const res = await onImport(file);
    setResult(res);
    setImporting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full"
      >
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Importer le catalogue</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {result ? (
            <div className="text-center">
              {result.status === 'completed' ? (
                <>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Import terminé !
                  </h3>
                  <p className="text-gray-600">
                    {result.imported_count} éléments importés sur {result.total_rows}
                  </p>
                  {result.error_count > 0 && (
                    <p className="text-orange-600 mt-2">
                      {result.error_count} erreurs
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle size={32} className="text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Erreur d'import
                  </h3>
                  <p className="text-gray-600">{result.error_message || 'Une erreur est survenue'}</p>
                </>
              )}
              <Button onClick={onClose} className="mt-6">Fermer</Button>
            </div>
          ) : (
            <>
              <div 
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <FileSpreadsheet size={48} className="mx-auto text-gray-400 mb-4" />
                {file ? (
                  <p className="text-gray-900 font-medium">{file.name}</p>
                ) : (
                  <>
                    <p className="text-gray-600 mb-2">
                      Cliquez pour sélectionner un fichier
                    </p>
                    <p className="text-sm text-gray-500">
                      Formats acceptés : CSV, Excel (.xlsx)
                    </p>
                  </>
                )}
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Format attendu</h4>
                <p className="text-sm text-blue-800">
                  Colonnes : <code>nom</code>, <code>description</code>, <code>prix</code>, 
                  <code>unité</code>, <code>catégorie</code>, <code>durée</code>
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={onClose}>Annuler</Button>
                <Button 
                  onClick={handleImport} 
                  disabled={!file || importing}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {importing ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <>
                      <Upload size={18} className="mr-2" />
                      Importer
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// Main Component
export default function CatalogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const token = localStorage.getItem('clientToken');

  useEffect(() => {
    fetchCatalog();
  }, []);

  const fetchCatalog = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/catalog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setItems(await res.json());
      }
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      const url = editingItem 
        ? `${API_URL}/api/vendor/catalog/${editingItem.id}`
        : `${API_URL}/api/vendor/catalog`;
      
      const res = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        toast.success(editingItem ? 'Modifié avec succès' : 'Ajouté avec succès');
        fetchCatalog();
        setShowFormModal(false);
        setEditingItem(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Supprimer cet élément ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/vendor/catalog/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Supprimé');
        setItems(items.filter(i => i.id !== itemId));
      }
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleImport = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/vendor/catalog/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const result = await res.json();
      if (result.status === 'completed') {
        fetchCatalog();
      }
      return result;
    } catch (e) {
      return { status: 'failed', error_message: 'Erreur de connexion' };
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = !searchQuery || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !selectedType || item.item_type === selectedType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catalog-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Package className="text-blue-600" size={28} />
            Catalogue
          </h1>
          <p className="text-gray-600 mt-1">
            {items.length} produits/services
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setShowImportModal(true)}>
            <Upload size={18} className="mr-2" />
            Importer
          </Button>
          <Button 
            onClick={() => { setEditingItem(null); setShowFormModal(true); }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={18} className="mr-2" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Tous les types</option>
          {ITEM_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Items Grid */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-12">
          <Package size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Aucun élément dans le catalogue</p>
          <Button 
            onClick={() => { setEditingItem(null); setShowFormModal(true); }}
            className="mt-4 bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={18} className="mr-2" />
            Ajouter ton premier élément
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => (
            <Card key={item.id} className="group hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      item.item_type === 'service' ? 'bg-blue-100 text-blue-700' :
                      item.item_type === 'product' ? 'bg-green-100 text-green-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {ITEM_TYPES.find(t => t.value === item.item_type)?.label || item.item_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => { setEditingItem(item); setShowFormModal(true); }}
                      className="p-1.5 hover:bg-gray-100 rounded"
                    >
                      <Edit2 size={16} className="text-gray-500" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1.5 hover:bg-red-100 rounded"
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </button>
                  </div>
                </div>
                
                <h3 className="font-semibold text-gray-900 mb-1 truncate">{item.name}</h3>
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">{item.description}</p>
                
                <div className="flex items-center justify-between text-sm">
                  {item.price ? (
                    <span className="font-semibold text-blue-600">
                      {item.price} {item.price_unit || '€'}
                    </span>
                  ) : item.price_min && item.price_max ? (
                    <span className="font-semibold text-blue-600">
                      {item.price_min} - {item.price_max} {item.price_unit || '€'}
                    </span>
                  ) : (
                    <span className="text-gray-400">Prix non défini</span>
                  )}
                  
                  {item.duration && (
                    <span className="text-gray-500 flex items-center gap-1">
                      <Clock size={14} />
                      {item.duration}
                    </span>
                  )}
                </div>
                
                {item.category && (
                  <div className="mt-2">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {item.category}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showFormModal && (
          <ItemFormModal
            item={editingItem}
            onClose={() => { setShowFormModal(false); setEditingItem(null); }}
            onSave={handleSave}
          />
        )}
        {showImportModal && (
          <ImportModal
            onClose={() => setShowImportModal(false)}
            onImport={handleImport}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
